import * as React from "react";
import sumBy from "lodash/sumBy";

import { definedMap } from "@glideapps/ts-necessities";

import type { ForceValueProducer, Value } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { LOADING } from "common/components/computation-types";
import {
    asArrayValue,
    isLoadedDefined,
    asString,
    asNumber,
    loadedDefinedMap,
    asBoolean,
} from "common/components/component-helpers";
import { mapFilterUndefined, isArray } from "common/Support";
import type { PalletteType } from "common/generator/components/color-types";
import { formatNumber } from "common/format-number";
import type { ChartDatum, ChartDatumWithTitle } from "../ui/smart/charts/minimal-chart/types";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { ErrorBoundary } from "lib/error-boundary";

const PieChart = lazilyLoadedComponent("pie-chart", () => import("../ui/smart/charts/pie-chart/pie-chart"));

export function makeChartDatumFromValue(v: Value, force: ForceValueProducer): ChartDatum {
    if (isArray(v)) {
        const [valueVP, displayValueVP] = v;
        const value = definedMap(valueVP, force);
        const displayValue = definedMap(displayValueVP, force);
        return {
            value: asNumber(value),
            displayValue: loadedDefinedMap(displayValue, asString),
        };
    } else {
        return { value: asNumber(v), displayValue: undefined };
    }
}

export function makeChartDataFromValues(list: Value, force: ForceValueProducer): ChartDatumWithTitle[] {
    return mapFilterUndefined(asArrayValue(list), vp => {
        const inspector = vp.getObjectInspector(force);
        if (!isLoadedDefined(inspector)) return undefined;

        const label = definedMap(inspector.getProperty("label"), force);
        const value = definedMap(inspector.getProperty("value"), force);

        if (!isLoadedDefined(label) || !isLoadedDefined(value)) return undefined;

        return {
            ...makeChartDatumFromValue(value, force),
            title: asString(label),
        };
    });
}

export const PieChartMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [
            list,
            titleValue,
            captionValue,
            unitValue,
            showTotalValue,
            totalValue,
            showLegendValue,
            colorValue,
            styleValue,
        ],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    if (list === LOADING) return env.renderProgress();
    if (list === undefined) return null;

    const title = loadedDefinedMap(titleValue, asString);
    const caption = loadedDefinedMap(captionValue, asString);
    const units = loadedDefinedMap(unitValue, asString);
    const showTotal = loadedDefinedMap(showTotalValue, asBoolean);
    let total = loadedDefinedMap(totalValue, asString);
    const showLegendByDefault = loadedDefinedMap(showLegendValue, asBoolean);
    const pallette: PalletteType = loadedDefinedMap(colorValue, asString) as PalletteType;
    const style = loadedDefinedMap(styleValue, asString);

    const data = makeChartDataFromValues(list, force).sort((d, e) => e.value - d.value);

    if (showTotal && total === undefined) {
        total = formatNumber(sumBy(data, x => x.value));
    }

    if (data.length === 0) return null;

    return (
        <ErrorBoundary>
            <React.Suspense fallback={<div style={{ width: 240, height: 240 }} />}>
                <PieChart
                    isDonut={style === "donut-chart"}
                    width={240}
                    height={240}
                    data={data}
                    title={title}
                    caption={caption}
                    units={units}
                    total={total}
                    pallette={pallette}
                    showLegendByDefault={showLegendByDefault}
                />
            </React.Suspense>
        </ErrorBoundary>
    );
};
